import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, Plus, Minus, Trash2, Package, Search } from 'lucide-react';
import { toast } from 'sonner';
import { customerApi } from '../../lib/api';

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({});
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    customerApi.get('/customer/products').then((r) => setProducts(r.data));
  }, []);

  const filtered = products.filter(
    (p) => !search.trim() || p.name.toLowerCase().includes(search.trim().toLowerCase()),
  );

  const add = (p) => {
    const current = cart[p.product_id]?.qty || 0;
    setCart({ ...cart, [p.product_id]: { product: p, qty: current + 1 } });
  };
  const setQty = (pid, qty) => {
    const q = Math.max(0, parseInt(qty) || 0);
    const c = cart[pid];
    if (!c) return;
    if (q === 0) {
      const next = { ...cart };
      delete next[pid];
      setCart(next);
    } else {
      setCart({ ...cart, [pid]: { ...c, qty: q } });
    }
  };
  const dec = (pid) => {
    const c = cart[pid];
    if (!c) return;
    if (c.qty <= 1) {
      const next = { ...cart };
      delete next[pid];
      setCart(next);
    } else {
      setCart({ ...cart, [pid]: { ...c, qty: c.qty - 1 } });
    }
  };
  const remove = (pid) => {
    const next = { ...cart };
    delete next[pid];
    setCart(next);
  };

  const cartItems = Object.entries(cart).map(([pid, c]) => ({ ...c, product_id: pid }));
  const totalUnits = cartItems.reduce((s, c) => s + c.qty, 0);

  const submit = async () => {
    if (cartItems.length === 0) return toast.error('Cart is empty');
    setSubmitting(true);
    try {
      const items = cartItems.map((c) => ({ product_id: c.product_id, qty: c.qty }));
      await customerApi.post('/customer/orders', { items, notes });
      toast.success('Quote request submitted! Our team will review and send you an invoice with pricing.');
      setCart({});
      setNotes('');
      navigate('/portal/orders');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fade-in space-y-6" data-testid="shop-page">
      <div>
        <h1 className="text-3xl font-semibold">Request a Quote</h1>
        <p className="text-sm text-[#6b7280] mt-1">
          Browse products and submit a quote request. Our team will confirm availability, apply your pricing, and send you a formal invoice.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Products */}
        <div className="lg:col-span-2 space-y-2">
          <div className="relative mb-3">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6b7280]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search products..."
              className="w-full pl-9 pr-3 py-2 border border-[#e7e7e7] rounded-lg text-sm"
              data-testid="shop-search-input"
            />
          </div>
          {filtered.length === 0 ? (
            <div className="card p-10 text-center text-sm text-[#6b7280]">
              <Package size={32} className="mx-auto mb-2 text-[#d4d4d4]" />
              No products found
            </div>
          ) : (
            filtered.map((p) => {
              const inCart = cart[p.product_id];
              return (
                <div
                  key={p.product_id}
                  className="card p-4 flex items-center justify-between"
                  data-testid={`prod-${p.product_id}`}
                >
                  <div className="flex-1 min-w-0 pr-4">
                    <div className="font-semibold truncate">{p.name}</div>
                    {(p.description || p.sku) && (
                      <div className="text-xs text-[#6b7280] mt-0.5 truncate">
                        {p.description || p.sku}
                      </div>
                    )}
                  </div>
                  {inCart ? (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => dec(p.product_id)}
                        className="w-8 h-8 rounded-full border border-[#e7e7e7] flex items-center justify-center"
                        data-testid={`dec-${p.product_id}`}
                      >
                        <Minus size={14} />
                      </button>
                      <input
                        type="number"
                        min="1"
                        value={inCart.qty}
                        onChange={(e) => setQty(p.product_id, e.target.value)}
                        className="w-14 text-center mono font-bold border border-[#e7e7e7] rounded-md py-1"
                        data-testid={`qty-${p.product_id}`}
                      />
                      <button
                        onClick={() => add(p)}
                        className="w-8 h-8 rounded-full bg-[#d92027] text-white flex items-center justify-center"
                        data-testid={`inc-${p.product_id}`}
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => add(p)}
                      className="btn-brand text-sm flex items-center gap-1"
                      data-testid={`add-${p.product_id}`}
                    >
                      <Plus size={14} /> Add
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Cart */}
        <div className="card p-5 sticky top-20 self-start" data-testid="cart">
          <div className="flex items-center gap-2 mb-4">
            <ShoppingCart size={18} />
            <h2 className="font-semibold">Your Request ({cartItems.length})</h2>
          </div>
          {cartItems.length === 0 ? (
            <div className="text-center text-sm text-[#6b7280] py-8">
              Add products to request a quote
            </div>
          ) : (
            <>
              <div className="space-y-2 max-h-[320px] overflow-y-auto mb-4">
                {cartItems.map((c) => (
                  <div
                    key={c.product_id}
                    className="flex items-center justify-between text-sm py-1"
                  >
                    <div className="flex-1 min-w-0 pr-2">
                      <div className="font-medium truncate">{c.product.name}</div>
                      <div className="text-xs text-[#6b7280]">Qty: {c.qty}</div>
                    </div>
                    <button
                      onClick={() => remove(c.product_id)}
                      className="text-[#d92027] p-1"
                      data-testid={`remove-${c.product_id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex justify-between mb-3 text-sm">
                <span className="text-[#6b7280]">Total units</span>
                <span className="mono font-bold">{totalUnits}</span>
              </div>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Delivery address, urgency, or other details (optional)"
                className="w-full px-3 py-2 border border-[#e7e7e7] rounded-lg text-sm mb-3"
                data-testid="order-notes"
              />
              <button
                onClick={submit}
                disabled={submitting}
                className="w-full btn-brand disabled:opacity-50"
                data-testid="place-order-btn"
              >
                {submitting ? 'Submitting...' : 'Submit Quote Request'}
              </button>
              <p className="text-xs text-[#6b7280] mt-2 text-center leading-relaxed">
                Our team will review, apply your agreed pricing, and send a formal invoice via email.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
